// PreferencesPanel — modal for display & accessibility preferences.
//
// Rendered from Nuggets.jsx when the sidebar Preferences button is
// clicked. Wires directly into the useAccessibility hook — no
// mid-layer state. Uses the same brand tokens (navy #0D1B2A + orange
// #E87722) as the rest of the app so it matches without a theme
// dependency.

import React from 'react';
import { X, Type, Zap, Contrast, Eye, LayoutGrid, RotateCcw } from 'lucide-react';

const ORANGE = '#E87722';
const NAVY   = '#0D1B2A';
const NAVY2  = '#132539';
const TEXT   = '#F0F4F8';
const MUTED  = 'rgba(255,255,255,0.55)';
const BORDER = 'rgba(255,255,255,0.08)';

function Row({ icon: Icon, label, hint, children }) {
  return (
    <div
      className="a11y-density-row"
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 1fr auto',
        alignItems: 'center',
        gap: 14,
        padding: '14px 4px',
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div style={{ color: MUTED, display: 'flex', justifyContent: 'center' }}>
        <Icon size={18} />
      </div>
      <div>
        <div style={{ color: TEXT, fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' }}>{label}</div>
        {hint && <div style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SegOption({ active, onClick, children, wide = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        background: active ? ORANGE : 'transparent',
        color: active ? '#fff' : TEXT,
        border: `1px solid ${active ? ORANGE : BORDER}`,
        borderRadius: 6,
        padding: wide ? '6px 12px' : '6px 10px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        fontFamily: "'Montserrat',sans-serif",
        transition: 'background 0.12s ease',
      }}
    >
      {children}
    </button>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(o => (
        <SegOption key={o.value} active={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </SegOption>
      ))}
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      style={{
        width: 42, height: 22, borderRadius: 999,
        background: value ? ORANGE : 'rgba(255,255,255,0.14)',
        border: 'none', position: 'relative', cursor: 'pointer',
        transition: 'background 0.15s ease',
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: value ? 22 : 2,
        width: 18, height: 18, borderRadius: '50%',
        background: '#fff', transition: 'left 0.15s ease',
      }} />
    </button>
  );
}

export default function PreferencesPanel({ prefs, update, reset, onClose }) {
  const onEscape = React.useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  React.useEffect(() => {
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [onEscape]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="a11y-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(6, 12, 22, 0.62)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, maxHeight: '86dvh', overflow: 'auto',
          background: NAVY, color: TEXT,
          border: `1px solid ${BORDER}`, borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${BORDER}`,
          background: NAVY2,
          borderTopLeftRadius: 12, borderTopRightRadius: 12,
        }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.14em', color: ORANGE, fontWeight: 700, textTransform: 'uppercase' }}>
              Display &amp; Accessibility
            </div>
            <div id="a11y-title" style={{ fontSize: 17, fontWeight: 800, marginTop: 3, letterSpacing: '-0.01em' }}>
              Preferences
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close preferences"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'transparent', border: `1px solid ${BORDER}`,
              color: MUTED, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Rows */}
        <div style={{ padding: '4px 20px 12px' }}>
          <Row
            icon={Type}
            label="Text size"
            hint="Scales the whole app. Larger sizes make dense screens breathable."
          >
            <Segmented
              value={prefs.fontSize}
              onChange={v => update({ fontSize: v })}
              options={[
                { value: 'sm',     label: 'A-' },
                { value: 'normal', label: 'A' },
                { value: 'lg',     label: 'A+' },
                { value: 'xl',     label: 'A++' },
              ]}
            />
          </Row>

          <Row
            icon={Zap}
            label="Reduce motion"
            hint="Removes animations and transitions. Auto follows your OS setting."
          >
            <Segmented
              value={prefs.reducedMotion}
              onChange={v => update({ reducedMotion: v })}
              options={[
                { value: 'auto', label: 'Auto' },
                { value: 'on',   label: 'On' },
                { value: 'off',  label: 'Off' },
              ]}
            />
          </Row>

          <Row
            icon={Contrast}
            label="High contrast"
            hint="Boosts contrast on text and UI surfaces. Photos stay untouched."
          >
            <Toggle
              value={prefs.highContrast}
              onChange={v => update({ highContrast: v })}
            />
          </Row>

          <Row
            icon={Eye}
            label="Focus ring"
            hint="A bolder outline on keyboard-focused elements."
          >
            <Segmented
              value={prefs.focusRing}
              onChange={v => update({ focusRing: v })}
              options={[
                { value: 'default', label: 'Default' },
                { value: 'bold',    label: 'Bold' },
              ]}
            />
          </Row>

          <Row
            icon={LayoutGrid}
            label="Density"
            hint="Tighter row padding for dense screens like Messenger."
          >
            <Segmented
              value={prefs.density}
              onChange={v => update({ density: v })}
              options={[
                { value: 'compact',     label: 'Compact' },
                { value: 'comfortable', label: 'Cozy' },
              ]}
            />
          </Row>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 18px',
          borderTop: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: NAVY2,
          borderBottomLeftRadius: 12, borderBottomRightRadius: 12,
        }}>
          <button
            type="button"
            onClick={reset}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: 'none',
              color: MUTED, fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
            }}
          >
            <RotateCcw size={12} />
            Reset to defaults
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: ORANGE, color: '#fff', border: 'none',
              padding: '8px 18px', borderRadius: 6,
              fontSize: 12, fontWeight: 800,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
